import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef
} from 'react';
import { useDocumentStorage } from './document-storage';
import { getAnalyticsWebClient } from '../utils/analytics';
import { EditorActions } from '@atlaskit/editor-core';
import { getShareUrlForData } from '../utils/query-data';

export type ActionSignup = (fromModal?: boolean) => void;

interface SandboxActions {
  signup: ActionSignup;
  share: () => void;
  setActions: (actions: EditorActions) => void;
}

const SandboxActionsContext = createContext<SandboxActions>({
  signup: () => {
    return;
  },
  share: () => {
    return;
  },
  setActions: () => {
    return;
  }
});

export const useSandboxActions = () => {
  const context = useContext(SandboxActionsContext);
  if (!context) {
    throw new Error(
      'useSandboxActions must be used within a SandboxActionsProvider'
    );
  }
  return context;
};

export const SandboxActionsProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { currentTemplateId, setDocument, save, title } = useDocumentStorage();
  const analyticClient = getAnalyticsWebClient();
  const actionsRef = useRef<EditorActions | null>(null);

  const setActions = (actions: EditorActions) => {
    actionsRef.current = actions;
  };

  const signup = useCallback(async (fromModal?: boolean) => {
    if (fromModal) {
      analyticClient.sendUIEvent({
        action: 'clicked',
        actionSubject: 'modalSignupButton',
        source: 'featureModal'
      });
    } else {
      analyticClient.sendUIEvent({
        action: 'clicked',
        actionSubject: 'signupButton',
        source: 'confluenceSandbox'
      });
    }

    if (actionsRef.current) {
      const value = await actionsRef.current.getValue();
      setDocument(value);
      if (save) {
        save(value);
      }
    }

    // open in new tab
    window.open(
      `https://www.atlassian.com/try/cloud/signup?bundle=confluence&edition=free&signupSource=sandbox${
        currentTemplateId.current
          ? `&templateId=${currentTemplateId.current}`
          : ''
      }`,
      '_blank'
    );
  }, []);

  const share = useCallback(async () => {
    analyticClient.sendUIEvent({
      action: 'clicked',
      actionSubject: 'shareButton',
      source: 'confluenceSandbox'
    });

    if (actionsRef.current) {
      const value = await actionsRef.current.getValue();
      const content = value.content;

      if (content) {
        const shareUrl = getShareUrlForData(content, title);
        console.log(shareUrl);
        // copy to clipboard
        navigator?.clipboard?.writeText(shareUrl).then(() => {
          console.log('Copied to clipboard');
        });
      }
    }
  }, [title]);

  return (
    <SandboxActionsContext.Provider
      value={{
        setActions,
        share,
        signup
      }}
    >
      {children}
    </SandboxActionsContext.Provider>
  );
};
